import React from 'react'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AlertCircle, Loader2 } from 'lucide-react'

// WHICH PAYMENT METHOD PAYS FOR WHICH HOTEL (docs/plans/2026-09-09-billing-payers.md §9.1, §10.2).
//
// ONE DIALOG, THE WHOLE GROUPING. The operator is answering a single question — here is how my
// hotels are split across the companies that pay for them — so the dialog submits every row every
// time and the backend applies them in one transaction. Unchanged rows are genuine no-ops there,
// which is what lets this surface stay simple: no dirty tracking, no partial save, no grouping
// nobody chose if one row is refused.
//
// AND SINCE §10.2 THE DEBT COMES WITH THE HOTELS, so a save can move real money between legal
// entities and charge a card in the same request. That earns a second step — but ONLY when it is
// true. Every Save runs the preview first:
//
//   • nothing is owed → the save goes straight through, exactly as it did before. No ceremony
//     where no money moves; a plain remap is still one click.
//   • money moves     → the body swaps to the server's own sentences, one per payment method,
//     rendered verbatim, and the operator commits from there.
//   • the server would refuse → the same sentence is shown BEFORE the attempt, with no Save to
//     press. A refusal the operator can read early is not a failed save.
//
// THE SENTENCES ARE THE SERVER'S. Amounts, deadlines and "will be charged now" are composed by
// App::Billing::DebtMove off the same pricing path the re-issued invoice bills on. This component
// performs no money math, no date math, and joins nothing into a sentence of its own.
//
// Each row's current value is SERVER-RESOLVED (`payerId` on the billing row), never matched by
// name in the browser: two hotels can share a name, two payment methods cannot share an id.

export interface OrganizeHotel {
  id: number
  name: string
  payerId: number | null
}

export interface OrganizeMethod {
  id: number
  name: string
}

/** The preview verdict, exactly as the backend answered it. */
export interface OrganizePreview {
  movesDebt: boolean
  /** Save would be refused with the one sentence below, so it is not offered. */
  blocked: boolean
  sentences: string[]
}

export interface OrganizeAssignment {
  property_id: number
  billing_profile_id: number
}

type Step = 'choose' | 'review'
type Busy = 'checking' | 'saving' | null

export default function OrganizeHotelsDialog({
  open,
  hotels,
  methods,
  error,
  onOpenChange,
  onPreview,
  onSubmit,
}: {
  open: boolean
  hotels: OrganizeHotel[]
  methods: OrganizeMethod[]
  /** The server's sentence when a request failed outright, rendered verbatim. */
  error: string | null
  onOpenChange: (open: boolean) => void
  /** Resolves with the server's verdict, or null when the request itself failed (`error` says so). */
  onPreview: (assignments: OrganizeAssignment[]) => Promise<OrganizePreview | null>
  /** Resolves true once the grouping is saved (the caller closes the dialog). */
  onSubmit: (assignments: OrganizeAssignment[]) => Promise<boolean>
}) {
  const fallbackMethodId = methods[0]?.id ?? null
  const [choices, setChoices] = React.useState<Record<number, string>>({})
  const [step, setStep] = React.useState<Step>('choose')
  const [preview, setPreview] = React.useState<OrganizePreview | null>(null)
  const [busy, setBusy] = React.useState<Busy>(null)

  // Re-seeded from the server's own grouping every time the dialog opens, so a cancelled edit
  // leaves nothing behind and a save made elsewhere is picked up on the next open. The step
  // resets with it: a dialog re-opened after a refusal starts at the selects, not at the verdict.
  React.useEffect(() => {
    if (!open) return
    const seeded: Record<number, string> = {}
    hotels.forEach((hotel) => {
      const id = hotel.payerId ?? fallbackMethodId
      if (id !== null) seeded[hotel.id] = String(id)
    })
    setChoices(seeded)
    setStep('choose')
    setPreview(null)
    setBusy(null)
  }, [open, hotels, fallbackMethodId])

  const assignments: OrganizeAssignment[] = hotels
    .map((hotel) => ({ property_id: hotel.id, billing_profile_id: Number(choices[hotel.id]) }))
    .filter((row) => Number.isFinite(row.billing_profile_id) && row.billing_profile_id > 0)

  // The whole flow, in the order the operator experiences it. The preview always runs — it is what
  // decides whether there is a second step at all, so the page never guesses from a status prop.
  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (busy) return

    if (step === 'review') {
      setBusy('saving')
      const saved = await onSubmit(assignments)
      if (!saved) setBusy(null)
      return
    }

    setBusy('checking')
    const verdict = await onPreview(assignments)
    if (!verdict) {
      setBusy(null)
      return
    }

    if (verdict.movesDebt || verdict.blocked) {
      setPreview(verdict)
      setStep('review')
      setBusy(null)
      return
    }

    setBusy('saving')
    const saved = await onSubmit(assignments)
    if (!saved) setBusy(null)
  }

  function back() {
    setStep('choose')
    setPreview(null)
  }

  // Esc and a click outside on the REVIEW step step backwards rather than closing: the operator
  // has spent real thought on the selects behind it, and losing them to a stray keypress would be
  // this dialog's one unforgivable move. A second Esc, from the selects, closes as normal.
  function handleOpenChange(next: boolean) {
    if (busy) return
    if (!next && step === 'review') {
      back()
      return
    }
    onOpenChange(next)
  }

  const blocked = step === 'review' && !!preview?.blocked
  const sentences = preview?.sentences ?? []

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {step === 'choose' ? 'Organize hotels' : blocked ? 'This grouping cannot be saved' : 'Before you save'}
          </DialogTitle>
          <DialogDescription>
            {step === 'choose' ? (
              <>
                Choose which payment method pays for each hotel. Invoices follow this grouping. Anything already due
                moves with its hotels when you save.
              </>
            ) : blocked ? (
              <>Nothing has changed yet.</>
            ) : (
              <>Money is already owed on these hotels. Here is what saving does.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <DialogBody className="space-y-4">
            {step === 'choose' ? (
              hotels.length === 0 ? (
                <p className="text-sm text-muted-foreground">There are no hotels on this business yet.</p>
              ) : (
                <div className="divide-y divide-border/40 overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
                  {hotels.map((hotel) => (
                    <div key={hotel.id} className="flex items-center justify-between gap-3 px-4 py-3">
                      <span className="min-w-0 truncate text-sm font-medium text-foreground">{hotel.name}</span>
                      <Select
                        value={choices[hotel.id] ?? ''}
                        onValueChange={(value) => setChoices((prev) => ({ ...prev, [hotel.id]: value }))}
                      >
                        <SelectTrigger
                          aria-label={`Payment method for ${hotel.name}`}
                          className="h-9 w-[190px] shrink-0"
                        >
                          <SelectValue placeholder="Choose one…" />
                        </SelectTrigger>
                        <SelectContent>
                          {methods.map((method) => (
                            <SelectItem key={method.id} value={String(method.id)}>
                              {method.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )
            ) : blocked ? (
              // One refusal sentence, and no Save beside it. Amber, because nothing is broken and
              // nothing has been lost — the grouping simply has to wait for this payment.
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{sentences[0]}</span>
              </div>
            ) : (
              <ul className="divide-y divide-border/40 overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
                {sentences.map((sentence) => (
                  <li key={sentence} className="px-4 py-3 text-sm text-foreground">
                    {sentence}
                  </li>
                ))}
              </ul>
            )}

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              className="rounded-lg"
              onClick={() => (step === 'review' ? back() : onOpenChange(false))}
              disabled={!!busy}
            >
              {step === 'review' ? 'Back' : 'Cancel'}
            </Button>
            {!blocked && (
              <Button
                type="submit"
                className="rounded-lg"
                disabled={!!busy || hotels.length === 0}
                aria-busy={!!busy}
              >
                {busy ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    {busy === 'checking' ? 'Checking…' : step === 'review' ? 'Moving…' : 'Saving…'}
                  </>
                ) : step === 'review' ? (
                  <>Save and move the money</>
                ) : (
                  <>Save grouping</>
                )}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
